// src/pages/AdminManagerAssignments.jsx
import { useEffect, useMemo, useState } from "react";
import Card from "../components/Card";
import Button from "../components/Button";
import { Input } from "../components/Input";
import { loadSession } from "../auth";
import {
  adminListUsers,
  adminAssignManager,
  adminUnassignManager,
  adminListReportsForManager,
} from "../services/api";

import { useTranslation } from "react-i18next";

function UserItem({ u, selected, onSelect }) {
  return (
    <label className={`flex items-center gap-3 p-2 rounded-lg cursor-pointer hover:bg-black/5 ${selected ? "bg-black/5" : ""}`}>
      <input type="radio" name="user" checked={selected} onChange={() => onSelect(u)} />
      <div className="flex-1">
        <div className="font-medium text-sm">{u.name || "—"}</div>
        <div className="text-xs text-black/60">{u.email}</div>
        <div className="text-xs text-black/50 mt-0.5">
          Manager: {u?.derived?.managerName || u?.manager?.name || u?.managerName || "—"}
        </div>
      </div>
    </label>
  );
}

function ManagerItem({ m, selected, onSelect }) {
  return (
    <label className={`flex items-center gap-3 p-2 rounded-lg cursor-pointer hover:bg-black/5 ${selected ? "bg-black/5" : ""}`}>
      <input type="radio" name="manager" checked={selected} onChange={() => onSelect(m)} />
      <div className="flex-1">
        <div className="font-medium text-sm">{m.name || "—"}</div>
        <div className="text-xs text-black/60">{m.email}</div>
        <div className="text-xs text-emerald-700 mt-0.5">Manager</div>
      </div>
    </label>
  );
}

export default function AdminManagerAssignments() {
  const me = loadSession()?.user;

  const { t } = useTranslation();

  const isOrgAdmin =
    me?.derived?.isOrgAdmin ||
    (Array.isArray(me?.roles) && me.roles.includes("org_super_admin"));

  // Data
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");

  const [users, setUsers] = useState([]);
  const [uq, setUq] = useState("");

  const [managers, setManagers] = useState([]);
  const [mq, setMq] = useState("");

  // Selection
  const [selectedUser, setSelectedUser] = useState(null);
  const [selectedManager, setSelectedManager] = useState(null);

  // Options
  const [autoPromote, setAutoPromote] = useState(true);
  const [replaceExisting, setReplaceExisting] = useState(true);

  // Reports (for selected manager)
  const [reports, setReports] = useState([]);
  const [reportsErr, setReportsErr] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!isOrgAdmin) {
      setLoading(false);
      return;
    }
    let alive = true;
    async function load() {
      setLoading(true);
      setErr("");
      try {
        // Load all users once
        const { items } = await adminListUsers({
          q: "",
          page: 1,
          pageSize: 500,
          isManager: "",
          includeDeactivated: false,
        });
        if (!alive) return;
        setUsers(items || []);
        setManagers((items || []).filter(u => !!(u?.derived?.isManager)));
      } catch (e) {
        if (!alive) return;
        setErr(e?.message || "Failed to load users");
      } finally {
        if (alive) setLoading(false);
      }
    }
    load();
    return () => { alive = false; };
  }, [isOrgAdmin]);

  // load reports for a manager (if backend supports /admin/team/reports?managerId=)
  useEffect(() => {
    if (!selectedManager?.id) {
      setReports([]);
      setReportsErr("");
      return;
    }
    let alive = true;
    async function load() {
      setReports([]);
      setReportsErr("");
      try {
        const items = await adminListReportsForManager(selectedManager.id);
        if (!alive) return;
        setReports(items || []);
      } catch (e) {
        if (!alive) return;
        // Graceful: backend might not support this yet
        setReportsErr("Direct reports unavailable for this manager.");
      }
    }
    load();
    return () => { alive = false; };
  }, [selectedManager?.id]);

  const filteredUsers = useMemo(() => {
    const term = uq.trim().toLowerCase();
    let list = users;
    if (term) {
      list = list.filter(
        (u) =>
          (u.name || "").toLowerCase().includes(term) ||
          (u.email || "").toLowerCase().includes(term)
      );
    }
    return list;
  }, [users, uq]);

  const filteredManagers = useMemo(() => {
    const term = mq.trim().toLowerCase();
    let list = managers;
    if (term) {
      list = list.filter(
        (m) =>
          (m.name || "").toLowerCase().includes(term) ||
          (m.email || "").toLowerCase().includes(term)
      );
    }
    return list;
  }, [managers, mq]);

  const assign = async () => {
    if (!selectedUser?.id) return alert("Pick a user.");
    if (!selectedManager?.id) return alert("Pick a manager.");
    setBusy(true);
    try {
      await adminAssignManager({
        managerId: selectedManager.id,
        reportId: selectedUser.id,
        autoPromoteManager: autoPromote,
        replaceExisting: replaceExisting,
      });
      alert(t("managerAssigned"));
    } catch (e) {
      alert(e?.message || "Assign failed");
    } finally {
      setBusy(false);
    }
  };

  const unassign = async () => {
    if (!selectedUser?.id) return alert(t("pickUser"));
    if (!selectedManager?.id) return alert(t("pickCurrentManager"));
    setBusy(true);
    try {
      await adminUnassignManager({
        managerId: selectedManager.id,
        reportId: selectedUser.id,
      });
      alert(t("managerUnassigned"));
    } catch (e) {
      alert(e?.message || "Unassign failed");
    } finally {
      setBusy(false);
    }
  };

  if (!isOrgAdmin) {
    return (
      <div className="max-w-6xl mx-auto px-4 py-6">
        <Card>
          <div className="text-sm text-black/70">
            {t("youneedsuperorgadmin")}
          </div>
        </Card>
      </div>
    );
  }

  return (
    <div className="max-w-6xl mx-auto px-4 py-6 space-y-4">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold">{t("managersAssignments")}</h1>
        <Button variant="ghost" onClick={() => window.location.reload()}>Refresh</Button>
      </div>

      {loading ? (
        <Card><div className="text-sm text-black/60">{t("loading")}</div></Card>
      ) : err ? (
        <Card><div className="text-sm text-red-600">{err}</div></Card>
      ) : (
        <div className="grid md:grid-cols-2 gap-4">
          {/* Users */}
          <Card title={t("users")}>
            <div className="mb-3">
              <Input
                placeholder={t("searchNameOrEmail")}
                value={uq}
                onChange={(e) => setUq(e.target.value)}
              />
            </div>
            <div className="max-h-[420px] overflow-auto space-y-1 pr-1">
              {filteredUsers.map((u) => (
                <UserItem
                  key={u.id}
                  u={u}
                  selected={selectedUser?.id === u.id}
                  onSelect={setSelectedUser}
                />
              ))}
            </div>
          </Card>

          {/* Managers */}
          <Card title={t("managers")}>
            <div className="mb-3">
              <Input
                placeholder={t("searchManagers")}
                value={mq}
                onChange={(e) => setMq(e.target.value)}
              />
            </div>
            <div className="max-h-[420px] overflow-auto space-y-1 pr-1">
              {filteredManagers.map((m) => (
                <ManagerItem
                  key={m.id}
                  m={m}
                  selected={selectedManager?.id === m.id}
                  onSelect={setSelectedManager}
                />
              ))}
            </div>

            {/* Selected manager's current reports */}
            {selectedManager?.id && (
              <div className="mt-4">
                <div className="text-sm font-medium mb-1">
                  {selectedManager.name || selectedManager.email} — {t("directReports")}
                </div>
                {reportsErr ? (
                  <div className="text-xs text-black/60">{reportsErr}</div>
                ) : (
                  <ul className="text-xs text-black/70 list-disc pl-5">
                    {(reports || []).length
                      ? reports.map((r) => (
                          <li key={r.id}>
                            {r.name || "—"} <span className="text-black/50">({r.email})</span>
                          </li>
                        ))
                      : <li className="text-black/50">{t("none")}</li>}
                  </ul>
                )}
              </div>
            )}
          </Card>
        </div>
      )}

      <Card>
        <div className="flex flex-wrap items-center gap-3">
          <Button onClick={assign} disabled={busy}>
            {busy ? t("assigningPending") : t("assignToSelectedUsers")}
          </Button>
          <Button onClick={unassign} variant="ghost" disabled={busy}>
            {busy ? t("unassigningPending") : t("unassignToSelectedUsers")}
          </Button>
          <label className="flex items-center gap-2 text-sm ml-2">
            <input
              type="checkbox"
              checked={autoPromote}
              onChange={(e) => setAutoPromote(e.target.checked)}
            />
            {t("autopromoteManager")}
          </label>
          <label className="flex items-center gap-2 text-sm">
            <input
              type="checkbox"
              checked={replaceExisting}
              onChange={(e) => setReplaceExisting(e.target.checked)}
            />
            {t("replaceExistingManager")}
          </label>
        </div>
      </Card>
    </div>
  );
}
